import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { Lang, getLang, setLangPersisted, translations } from './i18n';
import { updateWorkerLang } from './api';

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  setLangFromBackend: (l: Lang) => void;
  setAuthToken: (t: string) => void;
  t: typeof translations['en'];
}

const LangContext = createContext<LangContextValue>({
  lang: 'en',
  setLang: () => {},
  setLangFromBackend: () => {},
  setAuthToken: () => {},
  t: translations['en'],
});

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getLang);
  const tokenRef = useRef<string>('');

  const setLang = (l: Lang) => {
    setLangPersisted(l);
    setLangState(l);
    if (tokenRef.current) {
      updateWorkerLang(l, tokenRef.current).catch(console.error);
    }
  };

  const setLangFromBackend = (l: Lang) => {
    // Only apply backend value when the user has no local preference yet.
    // Once the user has explicitly toggled, localStorage is authoritative.
    if (!localStorage.getItem('lang')) {
      setLangPersisted(l);
      setLangState(l);
    }
  };

  const setAuthToken = (t: string) => {
    tokenRef.current = t;
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, setLangFromBackend, setAuthToken, t: translations[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
